import {
  Asset,
  Button,
  Checkbox,
  InfoIcon,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@clearcover/atlas';
import {
  CoveredPartyPayloadType,
  CreateOrUpdateDriverResponseType,
  DriverType,
  ExcludedDriverType,
  GetPolicyDraftResponseType,
  GetPolicyResponseTypeV2,
} from '@clearcover/core-app-types';
import { DriverAccidentType } from '@clearcover/core-app-types/dist/endpoints/getPolicy/driver';
import {
  editDriverPermissionsPolicy,
  removeDriverPermissionsPolicy,
} from 'Admin/authPolicies';
import { useAuth0 } from 'Auth';
import { draftEndpointModifier, PolicyContext } from 'Policies/utils';
import React, { FC, useContext, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Card, Column, FormInput, Row } from 'Shared/components';
import { DatePickerModal } from 'Shared/components/DatePickerModal';
import { useUnderwritingErrors } from 'Shared/hooks/useUnderwritingErrors';
import { useLazyApi } from 'Shared/services/Api';
import { useNewApiV2 } from 'Shared/services/Api/makeApiHttp';
import { DateTime } from 'luxon';
import {
  formatDateHyphenated,
  formatDateShort,
  formatMoney,
} from 'Shared/utils';
import {
  DriverOptionsResponse,
  GET_DRIVER_VEHICLES_TO_PAYLOAD,
} from '../PolicyDriversSection/DriverForm';
import { makeCreateUpdateDriverPayload } from '../PolicyDriversSection/DriverForm/utils';
import styles from './style.module.css';
import { ConflictingDraftModal } from '../ConflictingDraftModal';

export type PolicyClaimsType = {
  policy: GetPolicyResponseTypeV2 | GetPolicyDraftResponseType['policyDraft'];
};

type DriverAccident = {
  id: string;
  challenge?: 'edited' | 'removed';
  source: string;
  accidentDate: string;
  rawAccidentDate: string;
  claimType: string | undefined;
  totalLoss: string;
  fault: string | undefined;
  isAtFault: boolean;
  driver: string;
  driverStatus: string;
  claimStatus?: string;
  driverId: string;
  operatorId?: string | null;
  classifications?: DriverAccidentType['classifications'];
  isExcludedDriver?: boolean;
};

type ClaimTypeEnum =
  | 'At Fault'
  | 'Not At Fault'
  | 'RA Only'
  | 'GLS Only'
  | 'COMP Only';

const lendingLossClaimClassificationType = (accident: DriverAccidentType) => {
  const classifications = accident.classifications;
  const claimClassificationType = classifications?.claimClassificationTypeV2;

  return classifications?.lendingLoss
    ? 'Lending Loss - ' + claimClassificationType
    : claimClassificationType;
};

const isAtFault = (isFault: boolean) => {
  return isFault ? 'At Fault' : 'Not At Fault';
};

const getAccidentDriver = (
  accident: DriverAccidentType,
  driverName: string,
  driverId: string
) => {
  const driverAccident: DriverAccident = {
    id: accident.id,
    challenge: accident.challenge,
    source: accident.source,
    accidentDate: formatDateShort(accident.accidentDate),
    rawAccidentDate: accident.accidentDate,
    claimType: lendingLossClaimClassificationType(accident),
    totalLoss: accident.damageAmount,
    fault: isAtFault(accident.isAtFault),
    isAtFault: accident.isAtFault,
    driver: !accident.classifications?.lendingLoss ? driverName : '',
    driverStatus: !accident.classifications?.lendingLoss
      ? 'Active'
      : 'non-listed driver',
    claimStatus: accident.claimStatus,
    driverId: driverId,
    operatorId: accident.operatorId,
    classifications: accident.classifications,
  };
  return driverAccident;
};

const excludedDriverStatus = (
  policyPartyRelationship: ExcludedDriverType['policyPartyRelationship']
) => (policyPartyRelationship === 'excluded_person' ? 'Excluded' : 'Listed');

export const PolicyClaimsSection: FC<PolicyClaimsType> = ({ policy }) => {
  const { drivers, excludedDrivers } = policy;
  const [editingClaim, setEditingClaim] = useState<DriverAccident | null>(null);
  const {
    systemOfRecord,
    isEndorseablePolicy,
    canEndorseDuringRegionalEvents,
    resourceEffectiveGrants,
  } = useContext(PolicyContext);
  const { user } = useAuth0();

  const availableDrivers = useMemo(
    () =>
      drivers
        .filter(
          (d: DriverType) =>
            d.policyPartyRelationship === 'named_insured' ||
            d.policyPartyRelationship === 'covered_person'
        )
        .reduce<Record<string, string>>(
          (acc, d: DriverType) => {
            acc[d.id] = `${d.person.name.firstName} ${d.person.name.lastName}`;
            return acc;
          },
          { unlisted_driver: 'Unlisted Driver' }
        ),
    [drivers]
  );

  const coveredAccidents = drivers.flatMap((driver: DriverType) =>
    driver.accidents
      .filter((accident) => accident.claimStatus !== 'Collapsed')
      .map((accident) => {
        const driverPerson = driver.person.name;
        const driverName = driverPerson.firstName + ' ' + driverPerson.lastName;
        return getAccidentDriver(accident, driverName, driver.id);
      })
  );

  const excludedAccidents = (excludedDrivers ?? []).flatMap(
    (driver: ExcludedDriverType) =>
      (driver.accidents ?? [])
        .filter((accident) => accident.claimStatus !== 'Collapsed')
        .map((accident) => {
          const driverPerson = driver.person.name;
          const driverName =
            driverPerson.firstName + ' ' + driverPerson.lastName;
          const mapped = getAccidentDriver(accident, driverName, driver.id);
          mapped.isExcludedDriver = true;
          if (!accident.classifications?.lendingLoss) {
            mapped.driverStatus = excludedDriverStatus(
              driver.policyPartyRelationship
            );
          }
          return mapped;
        })
  );

  const accidentsDriver = [...coveredAccidents, ...excludedAccidents].sort(
    (a, b) =>
      new Date(b.rawAccidentDate).getTime() -
      new Date(a.rawAccidentDate).getTime()
  );

  const showEditDriverButtons =
    systemOfRecord === 'clearcover' &&
    (user?.isCarrierUser || user?.isSystemUser) &&
    canEndorseDuringRegionalEvents &&
    isEndorseablePolicy &&
    (removeDriverPermissionsPolicy(resourceEffectiveGrants) ||
      editDriverPermissionsPolicy(resourceEffectiveGrants));

  return (
    <div>
      {
        <Card id={'claims-card'}>
          <h2>Claims</h2>
          <ul className={styles.list}>
            <li key="title" className={styles.headers}>
              <span className={styles.header}>CLAIM STATUS</span>
              <span className={styles.header}>SOURCE</span>
              <span className={styles.header}>ACCIDENT DATE</span>
              <span className={styles.header}>CLAIM TYPE</span>
              <span className={styles.header}>TOTAL LOSS</span>
              <span className={styles.header}>FAULT</span>
              <span className={styles.header}>OPERATOR</span>
              <span className={styles.header}>DRIVER STATUS</span>
              <span className={styles.header}></span>
            </li>
            {accidentsDriver.length !== 0 ? (
              accidentsDriver.map((claim) => {
                return (
                  <li
                    data-testid={`claim-${claim.id}`}
                    key={`${claim.id}`}
                    className={styles.claim}
                  >
                    <div className={styles.data}>{claim.claimStatus}</div>
                    <div className={styles.data}>
                      {claim.source.toUpperCase()}
                    </div>
                    <div className={styles.data}>{claim.accidentDate}</div>
                    <div className={styles.data}>{claim.claimType}</div>
                    <div className={styles.data}>
                      {formatMoney(claim.totalLoss)}
                    </div>
                    <div className={styles.data}>{claim.fault}</div>
                    <div className={styles.data} title={claim.driver}>
                      {claim.driver}
                    </div>
                    <div className={styles.data}>{claim.driverStatus}</div>
                    <div className={styles.data} style={{ textAlign: 'right' }}>
                      {showEditDriverButtons && !claim.isExcludedDriver && (
                        <ConflictingDraftModal
                          onApprove={() => {
                            if (editingClaim) {
                              setEditingClaim(null);
                            } else {
                              setEditingClaim(claim);
                            }
                          }}
                          renderToggle={(openModal) => (
                            <Button
                              data-testid={'edit-claim-button'}
                              variant="secondary"
                              size="sm"
                              onClick={openModal}
                              disabled={
                                !!editingClaim || claim.challenge === 'removed'
                              }
                            >
                              Edit
                            </Button>
                          )}
                        />
                      )}
                    </div>
                  </li>
                );
              })
            ) : (
              <li data-testid="claim-dash" className={styles.claim}>
                <div className={styles.data}>-</div>
                <div className={styles.data}>-</div>
                <div className={styles.data}>-</div>
                <div className={styles.data}>-</div>
                <div className={styles.data}>-</div>
                <div className={styles.data}>-</div>
                <div className={styles.data}>-</div>
                <div className={styles.data}>-</div>
              </li>
            )}
          </ul>
        </Card>
      }
      {editingClaim && (
        <ChallengeClaimModal
          editingClaim={editingClaim}
          setEditingClaim={setEditingClaim}
          policy={policy}
          availableDrivers={availableDrivers}
        />
      )}
    </div>
  );
};

const ChallengeClaimModal: FC<{
  editingClaim: DriverAccident;
  setEditingClaim: React.Dispatch<React.SetStateAction<DriverAccident | null>>;
  policy: GetPolicyResponseTypeV2 | GetPolicyDraftResponseType['policyDraft'];
  availableDrivers: Record<string, string>;
}> = ({ editingClaim, setEditingClaim, policy, availableDrivers }) => {
  const isAddDriver = !(
    policy.drivers.find(({ id }) => id === editingClaim.driverId)
      ?.driversLicense.status === 'Learner'
  );

  const { details, flags, refetchHandler, driverOptions } =
    useContext(PolicyContext);
  const assumeAtFault = driverOptions?.accidents?.assumeAtFault ?? null;
  const { id: policyId, draftId } = useParams<{
    id: string;
    draftId: string;
  }>();

  const [{ loading: submitting }, challengeAccident] =
    useLazyApi<CreateOrUpdateDriverResponseType>({
      method: 'POST',
      url: `/policies/${policy.policyDetails.policyId}/challengeAccident/${
        editingClaim.id
      }${draftEndpointModifier(details.draftType, flags.renewalQuotesEnabled)}`,
    });

  const [{ loading: submittingRemove }, createOrUpdateDriver] =
    useLazyApi<CreateOrUpdateDriverResponseType>({
      method: 'POST',
      url: `/policies/${
        policy.policyDetails.policyId
      }/createOrUpdateDriver${draftEndpointModifier(
        details.draftType,
        flags.renewalQuotesEnabled
      )}`,
    });

  const [{ data: configDiscoveryParams }] = useNewApiV2<DriverOptionsResponse>({
    url: `/options/driver?configDiscoveryParams=${details.configDiscoveryParams}&productFilingIdentifier=${details.filingIdentifier}&isAddDriver=${isAddDriver}`,
  });

  const { setUnderwritingErrors, underwritingErrors } = useUnderwritingErrors();
  const history = useHistory();

  const [claimData, setClaimData] = useState<{
    accidentDate: string;
    claimClassificationType?: ClaimTypeEnum;
    damageAmount: string;
    isAtFault: boolean;
    operatorId?: string | null;
  }>({
    accidentDate: editingClaim.rawAccidentDate,
    claimClassificationType:
      editingClaim.classifications?.claimClassificationTypeV2,
    isAtFault: editingClaim.isAtFault,
    damageAmount: editingClaim.totalLoss,
    operatorId: editingClaim.operatorId,
  });

  const claimTypes: Record<string, string> =
    claimData.operatorId === 'unlisted_driver' ||
    (!claimData.operatorId && !!editingClaim.classifications?.lendingLoss)
      ? {
          'At Fault': 'Lending Loss - At Fault',
          'Not At Fault': 'Lending Loss - Not At-Fault',
          'COMP Only': 'Lending Loss - COMP Only',
          'GLS Only': 'Lending Loss - Glass Only',
        }
      : {
          'RA Only': 'RA Only',
          'COMP Only': 'COMP Only',
          'GLS Only': 'Glass Only',
          'At Fault': 'At-Fault Accident',
          'Not At Fault': 'Not At-Fault Accident',
        };

  const handleChallenge = (action: 'edit' | 'remove') => {
    if (action === 'edit') {
      const isLendingLoss =
        claimData.operatorId === 'unlisted_driver' ||
        (!claimData.operatorId && !!editingClaim.classifications?.lendingLoss);
      const destinationPartyId =
        isLendingLoss || claimData.operatorId === editingClaim.driverId
          ? null
          : (claimData.operatorId ?? null);

      challengeAccident({
        data: {
          sourcePartyId: editingClaim.driverId,
          destinationPartyId,
          isLendingLoss,
          date: formatDateHyphenated(claimData.accidentDate),
          isAtFault: claimData.isAtFault,
          damageAmount: claimData.damageAmount.length
            ? formatMoney(claimData.damageAmount, {
                includeCents: true,
                formatWithSymbol: false,
              }).replace(/,/g, '')
            : null,
          claimClassificationTypeV2: claimData.claimClassificationType ?? null,
        },
      })
        .then((draft) => {
          draftId === draft.data.draftId
            ? refetchHandler()
            : history.push(`/policies/${policyId}/draft/${draft.data.draftId}`);
        })
        .catch((status) => {
          setUnderwritingErrors(status);
        });
    } else {
      let driverToUpdate = {
        ...policy.drivers.find(({ id }) => id === editingClaim.driverId),
      };

      const rawRequestData: CoveredPartyPayloadType =
        GET_DRIVER_VEHICLES_TO_PAYLOAD(
          policy.vehicles,
          configDiscoveryParams,
          driverToUpdate as DriverType,
          details?.region
        );

      rawRequestData.reports.accidents[
        editingClaim.id as keyof typeof rawRequestData.reports.accidents
      ].challenge = 'removed';

      const payloadData = makeCreateUpdateDriverPayload({
        values: rawRequestData,
        type: 'update',
        requiredAccessStatements: details.requiredAccessStatements ?? false,
        excludedDriverOptionsQuestionsConfig:
          configDiscoveryParams.excludedDriverOptionsQuestionsConfig,
      });

      createOrUpdateDriver({ data: payloadData })
        .then((draft) => {
          draftId === draft.data.draftId
            ? refetchHandler()
            : history.push(`/policies/${policyId}/draft/${draft.data.draftId}`);
        })
        .catch((status) => {
          setUnderwritingErrors(status);
        });
    }
  };

  const edited =
    claimData.operatorId !== editingClaim.operatorId ||
    claimData.accidentDate !== editingClaim.rawAccidentDate ||
    claimData.damageAmount !== editingClaim.totalLoss ||
    claimData.isAtFault !== editingClaim.isAtFault ||
    (editingClaim.classifications
      ? claimData.claimClassificationType !==
        editingClaim.classifications.claimClassificationTypeV2
      : false);

  return (
    <Modal
      controlled={true}
      open={!!editingClaim}
      handleClose={() => setEditingClaim(null)}
      variant={'xl'}
      data-testid="chalenge-claim-modal"
    >
      {(closeModal) => (
        <div
          data-testid={'chalenge-claim-form'}
          className={styles.modalContainer}
        >
          <ModalHeader className={styles.modalHeader}>
            <h5>Edit Claim</h5>
          </ModalHeader>
          <ModalContent
            style={{ padding: '16px' }}
            className={styles.modalContent}
          >
            <Row>
              <Column variant="quarter" style={{ marginBottom: '10px' }}>
                Source: <b>{editingClaim.source.toUpperCase()}</b>
              </Column>
            </Row>
            <Row>
              <Column variant="quarter">
                <DatePickerModal
                  showEffectiveTime={false}
                  label="Accident Date"
                  startDate={
                    new Date(`${claimData.accidentDate.slice(0, 10)}T12:00:00`)
                  }
                  // Atlas returns UTC-midnight JS Dates. Reading in UTC gives the
                  // correct calendar date regardless of browser timezone.
                  dateFormatter={(date) =>
                    DateTime.fromJSDate(date, { zone: 'UTC' }).toISODate()!
                  }
                  onDateChange={(date) => {
                    setClaimData({ ...claimData, accidentDate: date });
                  }}
                />
              </Column>
              <Column variant="quarter">
                <FormInput
                  name="operator"
                  label="Operator"
                  data-testid="operator-field"
                  variant="select"
                  options={availableDrivers}
                  value={claimData.operatorId ?? ''}
                  onChange={(value: string) => {
                    setClaimData({
                      ...claimData,
                      operatorId: value,
                    });
                  }}
                />
              </Column>
              {editingClaim.classifications && (
                <Column variant="quarter">
                  <FormInput
                    name="claim-type"
                    label="Select Claim Type"
                    data-testid="claim-type-field"
                    variant={'select'}
                    options={claimTypes}
                    value={claimData.claimClassificationType}
                    onChange={(value: string) => {
                      setClaimData({
                        ...claimData,
                        claimClassificationType: value as ClaimTypeEnum,
                      });
                    }}
                  />
                </Column>
              )}
              <Column variant="sixth">
                <FormInput
                  label="Loss Amount"
                  name="loss-amount"
                  data-testid="loss-amount-field"
                  variant="text"
                  type="number"
                  value={claimData.damageAmount}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setClaimData({
                      ...claimData,
                      damageAmount: event.currentTarget.value,
                    });
                  }}
                />
              </Column>
            </Row>
            {!assumeAtFault && (
              <Row>
                <Column variant="full">
                  <Checkbox
                    name="at-fault"
                    value={claimData.isAtFault}
                    label="This was an at-fault accident"
                    data-testid="accident-at-fault-field"
                    onChange={(value: boolean) =>
                      setClaimData({ ...claimData, isAtFault: value })
                    }
                    className={styles.atFaultCheckbox}
                  />
                </Column>
              </Row>
            )}
            <div className={styles.info}>
              <Asset
                className={styles.infoIcon}
                icon={InfoIcon}
                height={16}
                width={16}
                description="info"
              />
              Please remember to submit proof in Zendesk when modifying claim
              data.
            </div>
            <p className={styles.warning}>Modifying data cannot be undone.</p>
            {underwritingErrors && (
              <p className={styles.warning}>{underwritingErrors}</p>
            )}
          </ModalContent>
          <ModalFooter>
            <div className={styles.modalButtonGroup}>
              <div>
                <Button
                  variant="primary"
                  style={{ marginRight: '16px' }}
                  onClick={() => handleChallenge('edit')}
                  data-testid="edit-claim-save-button"
                  disabled={!edited || submitting || submittingRemove}
                >
                  Save
                </Button>
                <Button
                  variant="danger"
                  onClick={() => handleChallenge('remove')}
                  data-testid="edit-claim-remove-button"
                  disabled={submitting || submittingRemove}
                >
                  Remove
                </Button>
              </div>
              <div>
                <Button variant="secondary" onClick={closeModal}>
                  Cancel
                </Button>
              </div>
            </div>
          </ModalFooter>
        </div>
      )}
    </Modal>
  );
};

export default PolicyClaimsSection;
